import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { MapContainer, TileLayer, CircleMarker } from "react-leaflet";
import api from "@/lib/api";
import {
  ArrowLeft, ExternalLink, Loader2, MapPin, DollarSign, Ruler, TrendingDown, Users,
  Sparkles, CheckCircle2, XCircle, Save,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const estadoLabels = {
  nuevo: "Nuevo", revisando: "Revisando", descartado: "Descartado",
  contactado: "Contactado", en_proceso: "En proceso", cerrado: "Cerrado",
};

const Stat = ({ icon: Icon, label, value, sub, accent = "text-slate-900", embedded = false }) => (
  <div className={`bg-white border border-slate-200 min-w-0 ${embedded ? "p-3" : "p-5"}`}>
    <div className={`flex items-center gap-2 ${embedded ? "mb-1.5" : "mb-2"}`}>
      <Icon className="w-3.5 h-3.5 text-slate-400 shrink-0" />
      <p className={`font-bold uppercase tracking-[0.12em] text-slate-500 truncate ${embedded ? "text-[9px]" : "text-[10px] tracking-[0.15em]"}`}>{label}</p>
    </div>
    <p className={`font-heading font-bold ${accent} leading-tight break-words ${embedded ? "text-lg" : "text-3xl leading-none"}`}>{value}</p>
    {sub && <p className={`text-slate-500 mt-1.5 break-words ${embedded ? "text-[11px] leading-tight" : "text-xs"}`}>{sub}</p>}
  </div>
);

export default function RadarFicha({ embeddedId, onClose: onCloseEmbedded }) {
  const { id: paramId } = useParams();
  const id = embeddedId || paramId;
  const embedded = Boolean(embeddedId);
  const navigate = useNavigate();
  const [o, setO] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingNotes, setSavingNotes] = useState(false);
  const [notas, setNotas] = useState("");
  const [openCierre, setOpenCierre] = useState(false);
  const [cierreForm, setCierreForm] = useState({ valor: "", moneda: "USD" });

  const load = async () => {
    try {
      const res = await api.get(`/opportunities/${id}`);
      setO(res.data);
      setNotas(res.data.notas || "");
    } catch (e) {
      toast.error("No se pudo cargar la oportunidad");
      navigate("/radar");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const changeEstado = async (nuevoEstado) => {
    try {
      const res = await api.patch(`/opportunities/${id}`, { estado: nuevoEstado });
      setO(res.data);
      toast.success(`Estado: ${estadoLabels[nuevoEstado]}`);
    } catch { toast.error("No se pudo cambiar el estado"); }
  };

  const saveNotas = async () => {
    setSavingNotes(true);
    try {
      const res = await api.patch(`/opportunities/${id}`, { notas });
      setO(res.data);
      toast.success("Notas guardadas");
    } catch { toast.error("No se pudieron guardar las notas"); }
    finally { setSavingNotes(false); }
  };

  const registrarCierre = async (e) => {
    e.preventDefault();
    if (!cierreForm.valor) return toast.error("Cargá el valor de la operación");
    try {
      const res = await api.post(`/opportunities/${id}/cierre`, {
        valor_operacion: Number(cierreForm.valor),
        moneda: cierreForm.moneda,
      });
      setO(res.data);
      setOpenCierre(false);
      toast.success("Cierre registrado");
    } catch (e) { toast.error(e.response?.data?.detail || "Error al registrar cierre"); }
  };

  if (loading) return <div className="p-12 text-slate-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Cargando...</div>;
  if (!o) return null;

  const ratioPct = o.ratio_vs_mediana ? Math.round((1 - o.ratio_vs_mediana) * 100) : null;
  const cover = o.fotos?.[0];
  // ── Regla de honestidad del scoring (ver services/scoring.py):
  //   • mediana_source === "barrio"   → afirmación de oportunidad (protagonista, verde/ámbar)
  //   • mediana_source === "localidad" o "provincia" → CONTEXTO (no afirmar)
  //     Mostramos el % con estilo tenue + badge "contexto" para que el user vea el dato pero
  //     no lo confunda con "subvaluada". Solo cambia el estilo, el cálculo es idéntico.
  const isContextRef = o.mediana_source && o.mediana_source !== "barrio";
  const refLevelLabel = o.mediana_source === "localidad" ? "localidad"
                       : o.mediana_source === "provincia" ? "provincia" : null;

  return (
    <div className={embedded ? "p-5 min-w-0" : "p-8 lg:p-12 max-w-7xl"}>
      <button data-testid="back-radar"
              onClick={() => embedded ? onCloseEmbedded?.() : navigate("/radar/tablero")}
              className="text-sm text-slate-500 hover:text-slate-900 flex items-center gap-1.5 mb-5">
        <ArrowLeft className="w-4 h-4" /> {embedded ? "Cerrar" : "Volver al tablero"}
      </button>

      {embedded ? (
        // ── Modo embebido (panel lateral 440px) ─────────────────────────────
        <header className="mb-5">
          <div className="flex items-center gap-1.5 mb-2 flex-wrap">
            <span className="text-[9px] uppercase tracking-[0.14em] font-bold text-slate-500">{o.tipo} · {o.operacion} · {o.fuente}</span>
          </div>
          {(o.es_subvaluado || o.es_dueno_directo) && (
            <div className="flex items-center gap-1.5 mb-3 flex-wrap">
              {o.es_subvaluado && (<span className="text-[9px] uppercase font-bold text-emerald-800 bg-emerald-100 border border-emerald-200 px-1.5 py-0.5"><Sparkles className="w-2.5 h-2.5 inline mr-0.5" />Subvaluada</span>)}
              {o.es_dueno_directo && (<span className="text-[9px] uppercase font-bold text-emerald-900 bg-emerald-200 border border-emerald-300 px-1.5 py-0.5"><Users className="w-2.5 h-2.5 inline mr-0.5" />Dueño directo</span>)}
            </div>
          )}
          <h1 className="font-heading text-lg font-bold tracking-tight text-slate-900 mb-2 leading-snug break-words">{o.titulo}</h1>
          <p className="text-xs text-slate-600 flex items-center gap-1.5">
            <MapPin className="w-3.5 h-3.5 shrink-0" />
            <span className="truncate">{o.localidad}{o.provincia ? `, ${o.provincia}` : ""}</span>
          </p>

          {/* HERO: descuento vs mediana + precio como protagonistas, score chico ──
              Solo `mediana_source === "barrio"` afirma oportunidad (color pleno).
              Localidad/provincia son CONTEXTO → estilo tenue + badge para no
              confundir al user con "subvaluada". Ver services/scoring.py. */}
          <div className={`mt-4 flex items-start justify-between gap-3 border p-4 ${
            isContextRef ? "bg-slate-50/60 border-slate-200 border-dashed"
                          : "bg-slate-50 border-slate-200"
          }`}>
            <div className="flex-1 min-w-0">
              {ratioPct != null ? (
                <>
                  <p className={`font-heading font-bold leading-none text-4xl ${
                    isContextRef
                      ? "text-slate-400"
                      : ratioPct >= 5 ? "text-emerald-700" : ratioPct < 0 ? "text-amber-700" : "text-slate-700"
                  }`}>
                    {ratioPct > 0 ? "−" : ratioPct < 0 ? "+" : ""}{Math.abs(ratioPct)}%
                  </p>
                  <p className={`text-[10px] uppercase tracking-[0.12em] font-bold mt-1.5 break-words ${
                    isContextRef ? "text-slate-400 italic" : "text-slate-500"
                  }`}>
                    {isContextRef && refLevelLabel && (
                      <span className="mr-1 inline-block px-1 py-0.5 border border-slate-300 text-slate-500 text-[9px] tracking-wider not-italic">
                        contexto · {refLevelLabel}
                      </span>
                    )}
                    vs mediana{o.ref_label ? ` · ${o.ref_label.replace(/^mediana\s+/i, "").replace(/^de\s+/i, "")}` : ""}
                  </p>
                </>
              ) : (
                <>
                  <p className="font-heading font-bold text-slate-400 text-2xl leading-none">sin ref.</p>
                  <p className="text-[10px] uppercase tracking-[0.12em] font-bold text-slate-500 mt-1.5">sin comparables suficientes</p>
                </>
              )}
              <p className="mt-3 font-heading text-xl font-bold text-slate-900 tabular-nums break-words">
                {o.moneda} {Number(o.precio).toLocaleString("es-AR")}
              </p>
            </div>
            <div className="shrink-0 text-right border-l border-slate-200 pl-3">
              <p className="font-heading text-base font-bold text-emerald-700 leading-none tabular-nums">{o.score || 0}</p>
              <p className="text-[9px] uppercase tracking-wider font-bold text-slate-400 mt-1">score</p>
            </div>
          </div>
        </header>
      ) : (
        <header className="mb-8 flex items-start justify-between flex-wrap gap-6">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              <span className="text-[10px] uppercase tracking-[0.15em] font-bold text-slate-500">{o.tipo} · {o.operacion} · {o.fuente}</span>
              {o.es_subvaluado && (<span className="text-[10px] uppercase font-bold text-emerald-800 bg-emerald-100 border border-emerald-200 px-2 py-0.5"><Sparkles className="w-3 h-3 inline mr-0.5" />Subvaluada</span>)}
              {o.es_dueno_directo && (<span className="text-[10px] uppercase font-bold text-emerald-900 bg-emerald-200 border border-emerald-300 px-2 py-0.5"><Users className="w-3 h-3 inline mr-0.5" />Dueño directo</span>)}
            </div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 mb-2">{o.titulo}</h1>
            <p className="text-sm text-slate-600 flex items-center gap-1.5">
              <MapPin className="w-4 h-4" />{o.localidad}{o.provincia ? `, ${o.provincia}` : ""}
            </p>
          </div>
          <div className="text-right">
            <p className="font-heading text-6xl font-bold text-emerald-700 leading-none">{o.score || 0}</p>
            <p className="text-xs uppercase tracking-wider font-bold text-slate-500 mt-2">Score</p>
          </div>
        </header>
      )}

      {/* Photos */}
      {o.fotos?.length > 0 && (
        embedded ? (
          <div className="mb-5">
            <div className="aspect-[16/10] bg-slate-100 overflow-hidden border border-slate-200 relative">
              <img src={cover} alt="" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = "none"; }} />
              {ratioPct != null && ratioPct >= 5 && !isContextRef && (
                <div className="absolute top-2.5 left-2.5 bg-emerald-700 text-white font-heading font-bold px-2 py-1 text-xs tracking-wide shadow">
                  −{ratioPct}% vs mediana
                </div>
              )}
            </div>
            {o.fotos.length > 1 && (
              <div className="grid grid-cols-3 gap-1.5 mt-1.5">
                {o.fotos.slice(1, 4).map((f) => (
                  <div key={f} className="aspect-square bg-slate-100 overflow-hidden border border-slate-200">
                    <img src={f} alt="" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = "none"; }} />
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-8">
            <div className="md:col-span-2 aspect-[16/10] bg-slate-100 overflow-hidden border border-slate-200 relative group">
              <img src={cover} alt="" className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500" onError={(e) => { e.target.style.display = "none"; }} />
              {ratioPct != null && ratioPct >= 5 && (
                <div className="absolute top-4 left-4 bg-emerald-700 text-white font-heading font-bold px-3 py-1.5 text-sm tracking-wide shadow-lg">
                  −{ratioPct}% vs mediana
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-1">
              {o.fotos.slice(1, 3).map((f) => (
                <div key={f} className="aspect-video bg-slate-100 overflow-hidden border border-slate-200 group">
                  <img src={f} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" onError={(e) => { e.target.style.display = "none"; }} />
                </div>
              ))}
            </div>
          </div>
        )
      )}

      {/* Stats — 2×2 en modo embebido para que entren en 440px */}
      <div className={embedded ? "grid grid-cols-2 gap-2 mb-2" : "grid grid-cols-2 lg:grid-cols-4 gap-4 mb-2"}>
        <Stat embedded={embedded} icon={DollarSign} label="Precio publicado" value={`${o.moneda} ${Number(o.precio).toLocaleString("es-AR")}`} />
        <Stat embedded={embedded} icon={Ruler} label="Superficie" value={`${o.superficie_m2} m²`} sub={`USD/m² ${Math.round(o.precio_m2 || 0)}`} />
        <Stat
          embedded={embedded}
          icon={TrendingDown}
          label="Mediana zona"
          value={o.precio_m2_mediana_zona ? `USD/m² ${Math.round(o.precio_m2_mediana_zona)}` : "—"}
          sub={ratioPct != null ? (ratioPct > 0 ? `${ratioPct}% por debajo` : `${-ratioPct}% arriba`) : "sin comparables"}
          accent={ratioPct != null && ratioPct >= 5 && !isContextRef ? "text-emerald-700" : ratioPct != null && ratioPct < 0 ? "text-amber-700" : "text-slate-900"}
        />
        <Stat embedded={embedded} icon={Users} label="Vendedor" value={o.tipo_vendedor === "dueno_directo" ? "Dueño directo" : o.tipo_vendedor === "inmobiliaria" ? "Inmobiliaria" : "Desconocido"}
              sub={o.vendedor || "—"} accent={o.es_dueno_directo ? "text-emerald-700" : "text-slate-900"} />
      </div>

      {/* Reference label — transparency: user siempre ve contra qué se está comparando.
          En modo embedded ya lo mostramos en el hero, así que lo escondemos para evitar redundancia. */}
      {o.ref_label && !embedded && (
        <div className="mb-8 mt-3 px-4 py-2.5 bg-slate-50 border-l-2 border-emerald-600 text-xs text-slate-700 flex flex-wrap items-center gap-x-3 gap-y-1"
             data-testid="ficha-ref-label">
          <span className="font-heading font-bold text-slate-900">{o.ref_label}</span>
          {o.ratio_vs_mediana != null && (
            <span className={o.ratio_vs_mediana < 1 ? "text-emerald-700 font-heading font-bold" : "text-amber-700 font-heading font-bold"}>
              · {o.ratio_vs_mediana < 1 ? "−" : "+"}{Math.abs(Math.round((1 - o.ratio_vs_mediana) * 100))}% vs referencia
            </span>
          )}
          {o.criterio_oportunidad && (
            <span className="ml-auto text-[10px] uppercase tracking-wider font-bold text-slate-500 bg-white border border-slate-200 px-2 py-0.5">
              Criterio: {o.criterio_oportunidad === "absoluto" ? "Umbral absoluto de zona" : "Umbral % global"}
            </span>
          )}
        </div>
      )}

      <div className={embedded ? "space-y-4" : "grid grid-cols-1 lg:grid-cols-3 gap-6"}>
        {/* Left col: motivos + mini map + link */}
        <div className={embedded ? "space-y-4" : "lg:col-span-2 space-y-6"}>
          {/* Motivos */}
          <section className="bg-white border border-slate-200 p-6">
            <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500 mb-4">Por qué es una oportunidad</p>
            {o.motivos?.length ? (
              <ul className="space-y-2">
                {o.motivos.map((m) => (
                  <li key={m} className="flex items-start gap-2 text-sm text-slate-800">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
                    <span>{m}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-slate-500">Sin señales especiales (score 0). Precio dentro de la mediana.</p>
            )}
          </section>

          {/* Mini map — se esconde cuando el panel está embebido (ya ves el
              pin en el mapa grande a la izquierda). */}
          {o.lat && o.lng && !embedded && (
            <section className="bg-white border border-slate-200 p-6">
              <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500 mb-4">Ubicación</p>
              <div style={{ height: 300 }} className="border border-slate-200">
                <MapContainer center={[o.lat, o.lng]} zoom={15} scrollWheelZoom={false} style={{ height: "100%", width: "100%" }}>
                  <TileLayer
                    url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
                    subdomains="abcd"
                    attribution='&copy; OSM &copy; CARTO'
                  />
                  <CircleMarker center={[o.lat, o.lng]} radius={14}
                    pathOptions={{ color: "#047857", fillColor: "#10b981", fillOpacity: 0.85, weight: 3 }} />
                </MapContainer>
              </div>
              <p className="text-xs text-slate-500 mt-3">{o.ubicacion_texto || "Ubicación aproximada (barrio)"}</p>
            </section>
          )}

          {/* Historial de estado */}
          <section className="bg-white border border-slate-200 p-6">
            <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500 mb-4">Historial de gestión</p>
            {o.estado_historial?.length ? (
              <ul className="space-y-3" data-testid="historial-list">
                {o.estado_historial.slice().reverse().map((h) => (
                  <li key={`${h.fecha}-${h.estado}`} className="flex items-baseline gap-3 text-sm border-l-2 border-emerald-200 pl-3">
                    <span className="font-semibold text-slate-900">{estadoLabels[h.estado] || h.estado}</span>
                    <span className="text-xs text-slate-500">{new Date(h.fecha).toLocaleString("es-AR")}</span>
                    {h.nota && <span className="text-xs text-slate-600 italic">— {h.nota}</span>}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-slate-500">Sin cambios de estado registrados.</p>
            )}
          </section>

          {/* Notas */}
          <section className="bg-white border border-slate-200 p-6">
            <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500 mb-4">Notas internas</p>
            <Textarea data-testid="notas-textarea" rows={4} value={notas} onChange={(e) => setNotas(e.target.value)}
                      placeholder="Contacté al vendedor, pidió pasar el jueves..."
                      className="rounded-none border-slate-300 focus:border-emerald-700 focus:ring-1 focus:ring-emerald-700" />
            <Button data-testid="save-notas" onClick={saveNotas} disabled={savingNotes} className="mt-3 bg-slate-900 hover:bg-slate-800 text-white rounded-none">
              {savingNotes ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Guardar notas
            </Button>
          </section>
        </div>

        {/* Right col: acciones */}
        <div className={embedded ? "space-y-4" : "space-y-6"}>
          <section className={`bg-white border border-slate-200 ${embedded ? "p-4" : "p-6 sticky top-6"}`}>
            <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500 mb-4">Estado</p>
            <Select value={o.estado} onValueChange={changeEstado}>
              <SelectTrigger data-testid="ficha-estado" className="rounded-none border-slate-300 h-10"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(estadoLabels).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <div className={`gap-2 mt-4 ${embedded ? "grid grid-cols-1" : "grid grid-cols-2"}`}>
              <Button data-testid="btn-contactar" variant="outline" onClick={() => changeEstado("contactado")} className="rounded-none border-slate-300 text-xs">Contactar</Button>
              <Button data-testid="btn-descartar" variant="outline" onClick={() => changeEstado("descartado")} className="rounded-none border-slate-300 text-xs">
                <XCircle className="w-3.5 h-3.5 mr-1" />Descartar
              </Button>
            </div>

            <div className="mt-6 pt-6 border-t border-slate-200">
              <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500 mb-3">Cierre</p>
              {o.cierre?.cerrada ? (
                <div className="bg-emerald-50 border border-emerald-200 p-3">
                  <p className="text-sm font-bold text-emerald-900">{o.cierre.moneda} {Number(o.cierre.valor_operacion).toLocaleString("es-AR")}</p>
                  <p className="text-xs text-emerald-800 mt-1">Cerrada el {new Date(o.cierre.fecha).toLocaleDateString("es-AR")}</p>
                </div>
              ) : (
                <Button data-testid="btn-cierre" onClick={() => setOpenCierre(true)} className="w-full bg-emerald-700 hover:bg-emerald-800 text-white rounded-none">
                  <CheckCircle2 className="w-4 h-4 mr-2" /> Registrar cierre
                </Button>
              )}
            </div>

            <div className="mt-6 pt-6 border-t border-slate-200">
              <a data-testid="link-aviso" href={o.url} target="_blank" rel="noreferrer"
                 className="text-sm text-emerald-800 hover:text-emerald-900 flex items-center gap-1.5">
                <ExternalLink className="w-4 h-4" /> Ver aviso original
              </a>
            </div>
          </section>
        </div>
      </div>

      <Dialog open={openCierre} onOpenChange={setOpenCierre}>
        <DialogContent className="rounded-none max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Registrar cierre</DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              Base para el cálculo de comisión futura.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={registrarCierre} className="space-y-4">
            <div>
              <Label className="text-xs font-bold uppercase tracking-[0.12em] text-slate-700">Valor de la operación</Label>
              <Input data-testid="cierre-valor" type="number" value={cierreForm.valor} onChange={(e) => setCierreForm({ ...cierreForm, valor: e.target.value })}
                     className="rounded-none border-slate-300 mt-1" />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-[0.12em] text-slate-700">Moneda</Label>
              <Select value={cierreForm.moneda} onValueChange={(v) => setCierreForm({ ...cierreForm, moneda: v })}>
                <SelectTrigger data-testid="cierre-moneda" className="rounded-none border-slate-300 mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="USD">USD</SelectItem>
                  <SelectItem value="ARS">ARS</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpenCierre(false)} className="rounded-none border-slate-300">Cancelar</Button>
              <Button type="submit" data-testid="cierre-submit" className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-none">Registrar</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
